import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { AuthProvider } from './lib/auth';
import { ThemeProvider } from './lib/config/theme';
import { ToastProvider } from './components/ui';
import { measureWebVitals } from './lib/config/performance';
import { captureAttribution } from './lib/attribution';
// Self-hosted fonts (previously Google Fonts CDN). Serving from our own origin
// means NO visitor IP reaches Google pre-consent — the LG München Google-Fonts
// ruling made CDN font loading a live GDPR damages pattern in the EU. Weights
// mirror the old <link>: Inter 300–800, Geist Mono 400–700, Instrument Serif
// regular + italic.
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist-mono/600.css';
import '@fontsource/geist-mono/700.css';
import '@fontsource/instrument-serif/400.css';
import '@fontsource/instrument-serif/400-italic.css';
import './index.css';

// ─── Stale-bundle self-recovery ─────────────────────────────────────────
// After a deploy, a returning user's cached index.html may reference asset
// hashes that no longer exist on the server. The browser then gets the
// SPA-fallback HTML back when it asks for a .js, throws a "Failed to
// fetch dynamically imported module" / chunk-load error, and the page
// renders blank. We catch those globally, unregister any stale SW, and
// hard-reload — so users self-recover instead of staring at a black screen.
const STALE_BUNDLE_RE = /Failed to fetch dynamically imported module|Loading chunk \d+ failed|Importing a module script failed|MIME type of "text\/html"/i;
function isStaleBundle(msg: unknown): boolean {
  return typeof msg === 'string' && STALE_BUNDLE_RE.test(msg);
}
async function recoverStaleBundle() {
  if (sessionStorage.getItem('waypoint-stale-recover')) return; // recovered once already this session — don't loop
  sessionStorage.setItem('waypoint-stale-recover', '1');
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } catch { /* best-effort */ }
  window.location.reload();
}
/**
 * Report an uncaught error to the self-hosted sink.
 *
 * ErrorBoundary already does this for React RENDER crashes, but these two global
 * handlers only ever checked isStaleBundle and dropped everything else on the floor —
 * so async errors, rejected fetches and event-handler throws (i.e. most real-world JS
 * errors) reached nobody. With one design partner that gap is total: the ops-watchdog
 * needs >50 distinct error types in an hour to page, so a single partner hitting a
 * broken screen is invisible either way unless the error is at least recorded.
 *
 * Body matches ErrorIngestBody in apps/api/src/routes/observability.ts. Fire-and-forget
 * and fully swallowed: error reporting must never itself throw into the error path.
 */
function reportUncaught(name: string, message: string, stack: string | undefined, kind: string): void {
  try {
    const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.waypoint-intel.com';
    void fetch(`${API_BASE}/v1/observability/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true, // survive a navigation away from the failing page
      body: JSON.stringify({
        source: 'client',
        level: 'error',
        name: (name || 'Error').slice(0, 200),
        message: (message || 'Unknown error').slice(0, 2000),
        stack: stack ? stack.slice(0, 5000) : undefined,
        url: window.location.href.slice(0, 500),
        context: { kind, userAgent: navigator.userAgent },
      }),
    })
      // A silently-swallowed beacon is how error_events went quiet without anyone
      // noticing. An HTTP-error RESPONSE (400/500) does NOT reject a fetch, so
      // check res.ok explicitly and make BOTH failure modes loud in the console.
      .then((res) => {
        if (!res.ok) console.warn(`[reportUncaught] error beacon rejected: HTTP ${res.status}`);
      })
      .catch((e) => { console.warn('[reportUncaught] error beacon unreachable', e); });
  } catch { /* never let reporting break the page */ }
}

window.addEventListener('error', e => {
  const ev = e as ErrorEvent;
  const msg = ev.message ?? String(ev.error ?? '');
  // A stale bundle is expected after a deploy and self-heals via reload — recover
  // rather than reporting it as a product error.
  if (isStaleBundle(msg)) { recoverStaleBundle(); return; }
  const err = ev.error as Error | undefined;
  reportUncaught(err?.name ?? 'UncaughtError', err?.message ?? msg, err?.stack, 'window.onerror');
});

window.addEventListener('unhandledrejection', e => {
  const reason = (e as PromiseRejectionEvent).reason;
  const msg = reason instanceof Error ? reason.message : String(reason ?? '');
  if (isStaleBundle(msg)) { recoverStaleBundle(); return; }
  reportUncaught(
    reason instanceof Error ? reason.name : 'UnhandledRejection',
    msg,
    reason instanceof Error ? reason.stack : undefined,
    'unhandledrejection',
  );
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 3,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 15000),
    },
  },
});

// Start measuring Core Web Vitals (production only)
if (import.meta.env.PROD) {
  measureWebVitals((vitals) => {
    if (vitals.lcp && vitals.fcp) {
      console.debug('[perf] Web Vitals:', vitals);
    }
  });
}

// First-touch signup attribution. Runs before the first render so the URL
// still carries ?utm_* and document.referrer still names the site that sent
// them here — React Router has not navigated yet. Idempotent per session.
captureAttribution();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
